import React from 'react'

import {
  MAX_ACTIVE_GROUP_MEMBERS_NUMBER,
  processVars,
} from '@smarty-nx/code-utils'
import { Typography, List, Checkmark, colors } from '@smarty-nx/ds'
import {
  DATA_LAYER_EVENTS,
  ORDER_TYPES,
  triggerProductEvent,
} from '@smarty-nx/tracking'

import { content } from './group-plan-benefits.content'
import classes from './group-plan-benefits.module.scss'
import { useBuildTimePlans, usePlansWithFallback } from '../../../hooks'
import { RootState, useAppSelector } from '../../../state/rootReducer'
import GroupShapes from '../../../static/images/groupShapes.png'
import { getFamilyPlansSlugs } from '../../../utils/plans'
import GroupLandingPageCTA from '../group-landing-page-cta'

const GroupPlanBenefits = () => {
  const buildTimePlans = useBuildTimePlans()
  const { list: plans, originalPlans } = usePlansWithFallback(
    getFamilyPlansSlugs(),
    buildTimePlans
  )
  const familyPlan = plans.find(
    (p) => p.priceData.id === 'SM_EVERYONE_UNLIMITED'
  )
  const familyPlanOriginal = originalPlans.find(
    (p) => p.id === 'SM_EVERYONE_UNLIMITED'
  )
  const membersNumber = useAppSelector(
    (state: RootState) => state.groupPlans.membersNumber
  )

  const groupOwnerPrice = familyPlan?.priceData.finalPrice.value || 0
  const groupMemberPrice = familyPlan?.priceData?.additionalSims?.value || 0

  return (
    <div className={classes.groupBenefitsRoot}>
      <Typography
        component="h2"
        variant="Kollektif/ExtraLarge"
        data-testid="group-plan-benefits-title"
      >
        {content.mainTitle}
      </Typography>
      <div className={classes.contentWrapper}>
        <div className={classes.sectionTextUnlimited}>
          <Typography
            component="h3"
            variant="Kollektif/HeaderText3XlBold"
            data-testid={`${content.sections[0].testIdPrefix}-title`}
          >
            {content.sections[0].title}
          </Typography>
          <Typography
            component="p"
            variant="Kollektif/Small"
            data-testid={`${content.sections[0].testIdPrefix}-description`}
          >
            {content.sections[0].description}
          </Typography>
        </div>
        <div className={classes.sectionContentUnlimited}>
          <List
            className={classes.list}
            data-testid={`${content.sections[0].testIdPrefix}-list`}
            items={content.sections[0].items.map((item) => ({
              content: processVars(item, {
                ownerPrice: String(groupOwnerPrice),
                memberPrice: String(groupMemberPrice),
                savings: String(
                  (groupOwnerPrice - groupMemberPrice) *
                    MAX_ACTIVE_GROUP_MEMBERS_NUMBER
                ),
              }),
              iconNode: (
                <Checkmark
                  primaryColor={colors['Brand/PalePrimaryGreen']}
                  secondaryColor={colors['Brand/PrimaryGreen']}
                  className={classes.listIcon}
                />
              ),
            }))}
          />
          <div className={classes.toolbar}>
            <div className={classes.iconBlock}>
              <img
                src={GroupShapes}
                alt={content.sections[0].iconAlt}
                className={classes.toolbarPicture}
                loading="lazy"
                decoding="async"
              />
              <p>{content.sections[0].iconCaption}</p>
            </div>
            <div className={classes.toolbarCta}>
              <GroupLandingPageCTA
                buttonType={content.sections[0].buttonType}
                dataTestIdPrefix="benefits"
                onClick={() => {
                  familyPlanOriginal &&
                    triggerProductEvent(
                      DATA_LAYER_EVENTS.ADD_TO_CART,
                      familyPlanOriginal,
                      membersNumber,
                      ORDER_TYPES.GROUPS_JOURNEY_ULFP
                    )
                }}
              />
            </div>
          </div>
        </div>
        <div className={classes.sectionTextMix}>
          <Typography
            component="h3"
            variant="Kollektif/HeaderText3XlBold"
            data-testid={`${content.sections[1].testIdPrefix}-title`}
          >
            {content.sections[1].title}
          </Typography>
          <Typography
            component="p"
            variant="Kollektif/Small"
            data-testid={`${content.sections[1].testIdPrefix}-description`}
          >
            {content.sections[1].description}
          </Typography>
        </div>
        <div className={classes.sectionContentMix}>
          <List
            className={classes.list}
            data-testid={`${content.sections[1].testIdPrefix}-list`}
            items={content.sections[1].items.map((item) => ({
              content: item,
              iconNode: (
                <Checkmark
                  primaryColor={colors['Brand/PalePrimaryGreen']}
                  secondaryColor={colors['Brand/PrimaryGreen']}
                  className={classes.listIcon}
                />
              ),
            }))}
          />
          <div className={classes.toolbar}>
            <div className={classes.iconBlock}>
              <img
                src={GroupShapes}
                alt={content.sections[1].iconAlt}
                className={classes.toolbarPicture}
                loading="lazy"
                decoding="async"
              />
              <p>{content.sections[1].iconCaption}</p>
            </div>
            <div className={classes.toolbarCta}>
              <GroupLandingPageCTA
                buttonType={content.sections[1].buttonType}
                dataTestIdPrefix="benefits"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GroupPlanBenefits
